import { CSRF_HEADER } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Ellipsis, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { conversationOf, HOME_PATH, navigate, useRoute } from "../router.tsx";
import { useSession } from "../session.tsx";
import { conversationKeys } from "./queries.ts";

/** The sidebar's "New chat": focus lands there after the open conversation is deleted. */
export const NEW_CHAT_ID = "sidebar-new-chat";

/** What a row or header menu needs to know of a conversation. */
export interface ConversationRef {
  id: string;
  title: string | null;
  createdBy: string;
  archivedAt: string | null;
}

/** Archives or unarchives (creator only); the lists follow. */
export function useArchive(id: string) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      const route = api.api.conversations[":conversationId"].archive;
      const args = [
        { param: { conversationId: id } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      ] as const;
      const res = archived ? await route.$post(...args) : await route.$delete(...args);
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(id) });
    },
  });
}

/**
 * "Delete" opens a consequence dialog: the shares that will lapse, and that files and drafts
 * saved from it stay. The confirm is `danger-solid`. Deleting the open conversation goes home,
 * with focus on the sidebar's "New chat".
 */
export function DeleteConversationDialog(props: {
  open: boolean;
  onClose: () => void;
  conversation: ConversationRef;
  returnFocus?: HTMLElement | null;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("deleteConversation.title")}
      returnFocus={props.returnFocus ?? null}
    >
      <DeleteForm conversation={props.conversation} onClose={props.onClose} />
    </AppDialog>
  );
}

function DeleteForm(props: { conversation: ConversationRef; onClose: () => void }) {
  const { conversation, onClose } = props;
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const route = useRoute();
  const shares = useQuery({
    queryKey: conversationKeys.shares(conversation.id),
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].shares.$get({
          param: { conversationId: conversation.id },
        }),
      ),
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await api.api.conversations[":conversationId"].$delete(
        { param: { conversationId: conversation.id } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: () => {
      const open = conversationOf(route) === conversation.id;
      onClose();
      queryClient.removeQueries({ queryKey: conversationKeys.detail(conversation.id) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      if (open) {
        navigate(HOME_PATH);
        // After the dialog returned focus to its opener (gone with the page).
        // Once the dialog is gone and has handed focus back (its opener went with the page).
        setTimeout(() => document.getElementById(NEW_CHAT_ID)?.focus(), 300);
      }
    },
  });
  const lapsing = shares.data?.length ?? 0;
  const refusal =
    remove.error instanceof ApiError && remove.error.code === "turn_in_progress"
      ? t("deleteConversation.turnInProgress")
      : remove.error?.message;
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-fg-secondary">
        <li>{t("deleteConversation.gone")}</li>
        {lapsing > 0 ? <li>{t("deleteConversation.lapsing", { count: lapsing })}</li> : null}
        <li>{t("deleteConversation.derived")}</li>
      </ul>
      {refusal ? <Notice tone="danger">{refusal}</Notice> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("app.cancel")}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
        >
          {remove.isPending ? t("deleteConversation.deleting") : t("deleteConversation.confirm")}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * The "…" actions of a conversation row (sidebar and lists): Archive or Unarchive and Delete,
 * for its creator only. Nothing renders for anyone else.
 */
export function ConversationRowMenu(props: {
  conversation: ConversationRef;
  className?: string;
  /**
   * -1 inside the sidebar's tree, which is one tab stop: keyboard users reach the same actions
   * in the conversation's header.
   */
  tabIndex?: number;
}) {
  const { conversation } = props;
  const { t } = useTranslation();
  const { userId } = useSession();
  const archive = useArchive(conversation.id);
  const [deleting, setDeleting] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (conversation.createdBy.toLowerCase() !== userId.toLowerCase()) {
    return null;
  }
  const title = conversation.title ?? t("conversation.untitled");
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={trigger}
            variant="ghost"
            size="icon-xs"
            aria-label={t("conversationList.actionsFor", { title })}
            tabIndex={props.tabIndex}
            className={cn("text-fg-muted hover:text-fg", props.className)}
          >
            <Ellipsis aria-hidden="true" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          {conversation.archivedAt ? (
            <DropdownMenuItem onSelect={() => archive.mutate(false)}>
              <ArchiveRestore aria-hidden="true" strokeWidth={1.75} />
              {t("conversationActions.unarchive")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => archive.mutate(true)}>
              <Archive aria-hidden="true" strokeWidth={1.75} />
              {t("conversationActions.archive")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>
            <Trash2 aria-hidden="true" strokeWidth={1.75} />
            {t("conversationActions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteConversationDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        conversation={conversation}
        returnFocus={trigger.current}
      />
    </>
  );
}
